import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, Music, Filter, X, ChevronRight, User, Key, Tag, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');
const TELUGU_VOWELS = 'అ ఆ ఇ ఈ ఉ ఊ ఎ ఏ ఐ ఒ ఓ ఔ'.split(' ');
const TELUGU_CONSONANTS = 'క ఖ గ ఘ చ ఛ జ ఝ ట ఠ డ ఢ త థ ద ధ న ప ఫ బ భ మ య ర ల వ శ స హ ళ ఱ'.split(' ');
const TELUGU_ALPHA = [...TELUGU_VOWELS, ...TELUGU_CONSONANTS, '#'];

// Hindi Devanagari letters for grouping
const HINDI_VOWELS = 'अ आ इ ई उ ऊ ए ऐ ओ औ'.split(' ');
const HINDI_CONSONANTS = 'क ख ग घ च छ ज झ ट ठ ड ढ त थ द ध न प फ ब भ म य र ल व श ष स ह'.split(' ');
const HINDI_ALPHA = [...HINDI_VOWELS, ...HINDI_CONSONANTS, '#'];

// Curated category tags for Indian language songs (from WordPress)
const SONG_CATEGORIES = [
  'Praise Songs', 'Worship Songs', 'Christmas Songs', 'Good Friday Songs',
  'Easter Songs', 'Prayer Songs', 'Gospel Songs', 'Encouraging Songs', 
  'Comfort Songs', 'Commitment Songs', 'Hope Songs', 'Thanksgiving Songs', 
  'Repentance Songs', 'Second Coming Songs', 'Correction Songs', 
  'Kids Songs', 'Marriage Songs', 'Offering Songs',
];

const LANGUAGES = [
  { code: 'English', label: 'English', flag: 'EN' },
  { code: 'Telugu', label: 'Telugu', flag: 'TE' },
  { code: 'Hindi', label: 'Hindi', flag: 'HI' },
];

const SongsPage = () => {
  const { language: urlLang } = useParams();
  const lang = LANGUAGES.find(l => l.code.toLowerCase() === (urlLang || 'english').toLowerCase())?.code || 'English';
  const isTelugu = lang === 'Telugu';
  const isHindi = lang === 'Hindi';
  const isIndian = isTelugu || isHindi;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [songs, setSongs] = useState([]);
  const [meta, setMeta] = useState({ categories: [], themes: [], songwriters: [], keys: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || '');
  const [activeKey, setActiveKey] = useState(searchParams.get('key') || '');
  const [activeLetter, setActiveLetter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSearchTerm('');
    setActiveCategory('');
    setActiveKey('');
    setActiveLetter('');
    Promise.all([
      fetch(`${BACKEND_URL}/api/songs?language=${lang}`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/songs/meta`).then(r => r.json())
    ]).then(([songsResp, metaData]) => {
      setSongs(songsResp.songs || songsResp);
      setMeta(metaData);
    }).catch(console.error).finally(() => setLoading(false));
  }, [lang]);

  // Get the first letter for grouping (Telugu, Hindi, or English)
  const getFirstLetter = useCallback((title) => {
    if (!title) return '#';
    const cp = title.codePointAt(0);
    if (isTelugu) {
      if (cp >= 0x0C05 && cp <= 0x0C14) return String.fromCodePoint(cp);
      if ((cp >= 0x0C15 && cp <= 0x0C39) || (cp >= 0x0C58 && cp <= 0x0C5A)) return String.fromCodePoint(cp);
      return '#';
    }
    if (isHindi) {
      if (cp >= 0x0905 && cp <= 0x0914) return String.fromCodePoint(cp);
      if (cp >= 0x0915 && cp <= 0x0939) return String.fromCodePoint(cp);
      return '#';
    }
    const ch = title[0].toUpperCase();
    return /[A-Z]/.test(ch) ? ch : '#';
  }, [isTelugu, isHindi]);

  const currentAlpha = isTelugu ? TELUGU_ALPHA : isHindi ? HINDI_ALPHA : ALPHA;

  const filtered = useMemo(() => {
    let result = songs;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(q) ||
        s.song_number.includes(searchTerm) ||
        (s.english_title && s.english_title.toLowerCase().includes(q)) ||
        (s.lyrics && s.lyrics.toLowerCase().includes(q)) ||
        (s.author && s.author.toLowerCase().includes(q)) ||
        (s.recording_artist && s.recording_artist.toLowerCase().includes(q))
      );
    }
    if (activeCategory) result = result.filter(s => s.category === activeCategory || (s.themes && s.themes.includes(activeCategory)));
    if (activeKey) result = result.filter(s => s.key_signature === activeKey);
    if (activeLetter) {
      if (isIndian) {
        if (activeLetter === '#') {
          result = result.filter(s => !currentAlpha.slice(0, -1).includes(getFirstLetter(s.title)));
        } else {
          result = result.filter(s => getFirstLetter(s.title) === activeLetter);
        }
      } else if (activeLetter === '#') {
        result = result.filter(s => /^[0-9]/.test(s.title));
      } else {
        result = result.filter(s => s.title.toUpperCase().startsWith(activeLetter));
      }
    }
    return result;
  }, [songs, searchTerm, activeCategory, activeKey, activeLetter, getFirstLetter, isTelugu]);

  const groupedByLetter = useMemo(() => {
    const groups = {};
    if (isIndian) {
      filtered.forEach(song => {
        const letter = getFirstLetter(song.title);
        const group = currentAlpha.includes(letter) ? letter : '#';
        if (!groups[group]) groups[group] = [];
        groups[group].push(song);
      });
      Object.values(groups).forEach(arr => arr.sort((a, b) => a.title.localeCompare(b.title)));
    } else {
      filtered.forEach(song => {
        const first = song.title[0].toUpperCase();
        const letter = /[A-Z]/.test(first) ? first : '#';
        if (!groups[letter]) groups[letter] = [];
        groups[letter].push(song);
      });
    }
    return groups;
  }, [filtered, isIndian, getFirstLetter, currentAlpha]);

  const availableLetters = useMemo(() => {
    if (isIndian) {
      const letters = new Set();
      songs.forEach(s => {
        const letter = getFirstLetter(s.title);
        if (currentAlpha.includes(letter)) letters.add(letter);
      });
      return letters;
    }
    const letters = new Set();
    songs.forEach(s => {
      const first = s.title[0].toUpperCase();
      letters.add(/[A-Z]/.test(first) ? first : '#');
    });
    return letters;
  }, [songs, isIndian, getFirstLetter, currentAlpha]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setActiveCategory('');
    setActiveKey('');
    setActiveLetter('');
    setSearchParams({});
  }, [setSearchParams]);

  // Compute category counts from song themes
  const teluguCategoryCounts = useMemo(() => {
    const counts = {};
    songs.forEach(s => {
      if (s.themes) {
        SONG_CATEGORIES.forEach(cat => {
          if (s.themes.includes(cat)) counts[cat] = (counts[cat] || 0) + 1;
        });
      }
    });
    return counts;
  }, [songs]);

  const hasActiveFilters = activeCategory || activeKey || activeLetter;

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="songs-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 font-medium">Loading hymnal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="songs-page">
      {/* Hero */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-2">
            <Music className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase">El Bethel Hymnal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3" data-testid="songs-title">
            Song Library
          </h1>
          <p className="text-stone-400 text-base sm:text-lg max-w-xl">
            {songs.length > 0 ? songs.length : meta.total} hymns and worship songs for praise, devotion, and corporate worship.
          </p>
          {/* Language Selector */}
          <div className="flex items-center gap-2 mt-4" data-testid="language-selector">
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                data-testid={`lang-btn-${l.code.toLowerCase()}`}
                onClick={() => navigate(`/songs/${l.code}`)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  lang === l.code
                    ? 'bg-amber-600 text-white'
                    : 'bg-white/10 text-stone-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                <Globe className="w-3.5 h-3.5" /> {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Search Bar */}
        <div className="relative -mt-6 mb-6 z-10">
          <div className="bg-white rounded-xl shadow-lg border border-stone-200 p-3 sm:p-4" data-testid="songs-search-bar">
            <div className="flex gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  data-testid="songs-search-input"
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={isIndian ? "Search in native script or English transliteration..." : "Search by title, lyrics, songwriter, or artist..."}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                data-testid="songs-filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  showFilters || hasActiveFilters
                    ? 'bg-amber-600 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {hasActiveFilters && (
                  <span className="bg-white/30 text-xs px-1.5 py-0.5 rounded-full">
                    {[activeCategory, activeKey, activeLetter].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-stone-100 space-y-4" data-testid="songs-filter-panel">
                {/* Theme/Category */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                    <Tag className="w-3.5 h-3.5" /> Category
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {SONG_CATEGORIES.filter(c => teluguCategoryCounts[c]).map(t => (
                      <button
                        key={t}
                        data-testid={`filter-theme-${t.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => setActiveCategory(activeCategory === t ? '' : t)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          activeCategory === t
                            ? 'bg-amber-600 text-white shadow-sm'
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {t} ({teluguCategoryCounts[t]})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Musical Key */}
                {meta.keys.length > 0 && (
                  <div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                      <Key className="w-3.5 h-3.5" /> Musical Key
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {meta.keys.map(k => (
                        <button
                          key={k}
                          data-testid={`filter-key-${k}`}
                          onClick={() => setActiveKey(activeKey === k ? '' : k)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            activeKey === k
                              ? 'bg-indigo-600 text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-3 h-3" /> Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Category Filters */}
        {Object.keys(teluguCategoryCounts).length > 0 && (
          <div className="mb-4 overflow-x-auto -mt-2" data-testid="telugu-category-filters">
            <div className="flex gap-1.5 min-w-max pb-1">
              {SONG_CATEGORIES.filter(c => teluguCategoryCounts[c]).map(cat => (
                <button
                  key={cat}
                  data-testid={`quick-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setActiveCategory(activeCategory === cat ? '' : cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    activeCategory === cat
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-700'
                  }`}
                >
                  {cat} <span className="opacity-60">{teluguCategoryCounts[cat]}</span>
                </button>
              ))}
              {activeCategory && (
                <button onClick={() => setActiveCategory('')} className="px-2 py-1.5 text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-0.5">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Alphabetical Index + Results */}
        <div className="flex gap-4 mb-12 pb-24 lg:pb-0">
          {/* Alpha sidebar - desktop */}
          {!isIndian ? (
          <div className="hidden lg:flex flex-col items-center gap-0.5 sticky top-24 self-start" data-testid="alpha-index">
            {ALPHA.map(letter => {
              const available = availableLetters.has(letter);
              const active = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => setActiveLetter(active ? '' : letter)}
                  disabled={!available && !active}
                  className={`w-8 h-7 flex items-center justify-center text-xs font-bold rounded transition-all ${
                    active
                      ? 'bg-amber-600 text-white scale-110'
                      : available
                        ? 'text-stone-600 hover:bg-stone-200 hover:text-stone-900'
                        : 'text-stone-300 cursor-default'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
          ) : (
          <div className="hidden lg:flex flex-col items-center gap-0.5 sticky top-24 self-start" data-testid="alpha-index-native" style={isTelugu ? {fontFamily: "'Noto Sans Telugu', sans-serif"} : {}}>
            {currentAlpha.map(letter => {
              const available = availableLetters.has(letter);
              const active = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => setActiveLetter(active ? '' : letter)}
                  disabled={!available && !active}
                  className={`w-8 h-7 flex items-center justify-center text-xs font-bold rounded transition-all ${
                    active
                      ? 'bg-amber-600 text-white scale-110'
                      : available
                        ? 'text-stone-600 hover:bg-stone-200 hover:text-stone-900'
                        : 'text-stone-300 cursor-default'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
          )}

          {/* Alpha bar - mobile */}
          <div className="lg:hidden fixed bottom-[64px] left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 px-2 py-1.5 overflow-x-auto" data-testid="alpha-index-mobile">
            <div className="flex gap-0.5 min-w-max justify-center" style={isTelugu ? {fontFamily: "'Noto Sans Telugu', sans-serif"} : {}}>
              {(isIndian ? currentAlpha : ALPHA).map(letter => {
                const available = availableLetters.has(letter);
                const active = activeLetter === letter;
                return (
                  <button
                    key={letter}
                    onClick={() => setActiveLetter(active ? '' : letter)}
                    disabled={!available && !active}
                    className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded transition-all ${
                      active
                        ? 'bg-amber-600 text-white'
                        : available
                          ? 'text-stone-600'
                          : 'text-stone-300'
                    }`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Song List */}
          <div className="flex-1 min-w-0">
            {/* Result count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-stone-500" data-testid="songs-count">
                {filtered.length === songs.length
                  ? `${songs.length} songs`
                  : `${filtered.length} of ${songs.length} songs`}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-white rounded-xl border border-stone-200 p-12 text-center" data-testid="songs-empty">
                <Music className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-stone-800 mb-1">No songs found</h3>
                <p className="text-stone-500 text-sm mb-4">Try adjusting your search or filters</p>
                <button onClick={clearFilters} className="text-amber-600 hover:text-amber-700 font-semibold text-sm">
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedByLetter).sort(([a], [b]) => {
                  if (isIndian) {
                    const ai = currentAlpha.indexOf(a);
                    const bi = currentAlpha.indexOf(b);
                    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                  }
                  return a.localeCompare(b);
                }).map(([letter, letterSongs]) => (
                  <div key={letter}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-black text-amber-600" style={isIndian ? (isTelugu ? {fontFamily: "'Noto Sans Telugu', sans-serif"} : {}) : {}}>{letter}</span>
                      <div className="flex-1 h-px bg-stone-200"></div>
                      <span className="text-xs text-stone-400 font-medium">{letterSongs.length}</span>
                    </div>
                    <div className="space-y-1">
                      {letterSongs.map(song => (
                        <button
                          key={song.id}
                          data-testid={`song-item-${song.id}`}
                          onClick={() => navigate(`/songs/${lang.toLowerCase()}/${song.id}`)}
                          className="w-full text-left bg-white rounded-lg border border-stone-100 hover:border-amber-200 hover:shadow-md p-3 sm:p-4 transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <span className="flex-shrink-0 w-10 h-10 bg-stone-100 group-hover:bg-amber-50 rounded-lg flex items-center justify-center text-stone-400 group-hover:text-amber-600 transition-colors">
                              <Music className="w-4 h-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-stone-800 group-hover:text-amber-700 truncate transition-colors" style={isTelugu ? {fontFamily: "'Noto Sans Telugu', sans-serif"} : {}}>
                                {song.title}
                              </h3>
                              {isIndian && song.english_title && (
                                <p className="text-xs text-stone-400 truncate">{song.english_title}</p>
                              )}
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {song.author && (
                                  <span className="text-xs text-stone-400 flex items-center gap-1 truncate max-w-[200px]">
                                    <User className="w-3 h-3 flex-shrink-0" /> {song.author}
                                  </span>
                                )}
                                {song.year && (
                                  <span className="text-xs text-stone-400">
                                    ({song.year})
                                  </span>
                                )}
                                {song.key_signature && (
                                  <span className="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded font-medium">
                                    {song.key_signature}
                                  </span>
                                )}
                                {song.lyrics_with_chords && !isIndian && (
                                  <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">
                                    Chords
                                  </span>
                                )}
                                {song.lyrics_with_chords && isIndian && (
                                  <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-medium">
                                    English
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-amber-500 transition-colors flex-shrink-0" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SongsPage;
